import React, { useRef } from 'react';
import { Card, CardBody, CardHeader } from '../../../../components/Atoms/Card';
import Badge from '../../../../components/Atoms/Badge';
import Button from '../../../../components/Atoms/Button';
import IconButton from '../../../../components/Atoms/IconButton';
import { useI18n } from '../../../../i18n';
import { formatBytes } from '../hooks/useVideoToImagesHook';

/**
 * Step 2 — the drop zone. Accepts individual files, a whole folder
 * (`webkitdirectory`), and .zip archives, which the server opens recursively.
 *
 * Progress mirrors the labeling pages' upload overlay: one bar driven by
 * axios `onUploadProgress`, since there is no chunked upload path in this
 * platform to report per-chunk state.
 */
const VideoUploadZone = ({
  selectedFiles,
  supportedExtensions,
  dragOver,
  isUploading,
  uploadPercent,
  uploadedBytes,
  handlers,
}) => {
  const { t } = useI18n();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const totalBytes = selectedFiles.reduce((sum, entry) => sum + (entry.file.size || 0), 0);
  const formats = (supportedExtensions.length
    ? supportedExtensions
    : ['.mp4', '.avi', '.mov', '.mkv']
  ).join(', ');

  const onInputChange = (event) => {
    handlers.handleFilesPicked(event.target.files);
    event.target.value = ''; // let the same file be picked again after removal
  };

  return (
    <Card className="v2i-card">
      <CardHeader
        title={t('videoToImages.upload.heading')}
        actions={
          selectedFiles.length ? (
            <div className="d-flex align-items-center">
              <Badge variant="primary" icon="fa-film" className="mr-2">
                {t('videoToImages.upload.selectedCount', { count: selectedFiles.length })}
              </Badge>
              <Button variant="ghost" size="sm" onClick={handlers.handleClearFiles} disabled={isUploading}>
                {t('videoToImages.upload.clearAll')}
              </Button>
            </div>
          ) : null
        }
      />
      <CardBody>
        <div
          className={`v2i-dropzone ${dragOver ? 'v2i-dropzone--active' : ''}`}
          onDragOver={handlers.handleDragOver}
          onDragLeave={handlers.handleDragLeave}
          onDrop={handlers.handleDrop}
        >
          <i className="fa fa-cloud-upload-alt v2i-dropzone__icon" aria-hidden="true"></i>
          <p className="v2i-dropzone__title">{t('videoToImages.upload.dropHere')}</p>
          <p className="v2i-dropzone__or">{t('videoToImages.upload.or')}</p>

          <div className="v2i-dropzone__actions">
            <Button variant="outline-primary" size="sm" disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}>
              <i className="fa fa-file-video mr-1" aria-hidden="true"></i>
              {t('videoToImages.upload.chooseFiles')}
            </Button>
            <Button variant="outline-secondary" size="sm" disabled={isUploading}
              onClick={() => folderInputRef.current?.click()}>
              <i className="fa fa-folder-open mr-1" aria-hidden="true"></i>
              {t('videoToImages.upload.chooseFolder')}
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="video/*,.zip,.mkv,.mov,.avi,.mp4"
            onChange={onInputChange}
            style={{ display: 'none' }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            webkitdirectory=""
            directory=""
            onChange={onInputChange}
            style={{ display: 'none' }}
          />

          <p className="v2i-hint mb-0">{t('videoToImages.upload.supported', { formats })}</p>
          <p className="v2i-hint mb-0">{t('videoToImages.upload.zipNote')}</p>
        </div>

        {isUploading && (
          <div className="v2i-upload-progress" role="status">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="v2i-upload-progress__label">
                {t('videoToImages.upload.uploading', {
                  name: selectedFiles.length === 1
                    ? selectedFiles[0].relativePath
                    : t('videoToImages.upload.selectedCount', { count: selectedFiles.length }),
                })}
              </span>
              <span className="v2i-upload-progress__value">
                {t('videoToImages.upload.uploadProgress', {
                  percent: uploadPercent,
                  size: formatBytes(totalBytes),
                })}
              </span>
            </div>
            <div className="v2i-progress">
              <div className="v2i-progress__fill" style={{ width: `${uploadPercent}%` }} />
            </div>
            <p className="v2i-hint mb-0 mt-1">{formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}</p>
          </div>
        )}

        {!!selectedFiles.length && (
          <div className="v2i-filelist">
            <div className="v2i-filelist__head">
              <span>{t('videoToImages.upload.selectedCount', { count: selectedFiles.length })}</span>
              <span>{t('videoToImages.upload.totalSize', { size: formatBytes(totalBytes) })}</span>
            </div>
            <ul className="v2i-filelist__items">
              {selectedFiles.map((entry) => (
                <li key={entry.relativePath} className="v2i-filelist__item">
                  <i className="fa fa-film v2i-filelist__icon" aria-hidden="true"></i>
                  <span className="v2i-filelist__name" title={entry.relativePath}>
                    {entry.relativePath}
                  </span>
                  <span className="v2i-filelist__size">{formatBytes(entry.file.size)}</span>
                  <IconButton
                    icon="fa-times"
                    title={t('common.remove')}
                    disabled={isUploading}
                    onClick={() => handlers.handleRemoveFile(entry.relativePath)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default VideoUploadZone;
